import { CurrencyInput, PercentInput } from '@client/components/NumericInput';
import { Button } from '@client/components/ui/button';
import { AccountDetailFormState } from '@client/hooks/useAccountDetailForm';
import { cn } from '@client/lib';
import { useCallback, useMemo, useRef, useState } from 'react';
import { StepProgressIndicator } from './StepProgressIndicator';

type AccountDetailFormErrors = Partial<Record<keyof AccountDetailFormState, string>>;

interface AccountDetailStepperProps {
  formState: AccountDetailFormState;
  formErrors: AccountDetailFormErrors;
  onChangeField: (field: keyof AccountDetailFormState) => (value: number | null) => void;
  onSubmit: () => void;
  onBack?: () => void;
}

const TOTAL_STEPS = 4;

interface StepConfig {
  field: keyof AccountDetailFormState;
  title: string;
  description: string | React.ReactNode;
  inputComponent: React.ComponentType<any>;
  inputProps: any;
  isRequired: boolean;
}

export function AccountDetailStepper({
  formState,
  formErrors,
  onChangeField,
  onSubmit,
  onBack,
}: AccountDetailStepperProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const steps: StepConfig[] = [
    {
      field: 'existingAssetsValue',
      title: 'Current Value',
      description:
        'Present-day value of the asset that can be reserved for future long-term care expenses',
      inputComponent: CurrencyInput,
      inputProps: {
        label: 'CURRENT VALUE OF ASSET',
        placeholder: 'e.g. $50,000',
      },
      isRequired: true,
    },
    {
      field: 'monthlyContributionAmount',
      title: 'Monthly Contribution',
      description: 'Money added each month to increase the total value available for LTC expenses',
      inputComponent: CurrencyInput,
      inputProps: {
        label: 'MONTHLY CONTRIBUTION',
        placeholder: 'e.g. $500',
      },
      isRequired: false,
    },
    {
      field: 'annualRateOfReturn',
      title: 'Annual Rate of Return',
      description:
        'The expected annual rate of return applied to both existing assets and monthly contributions',
      inputComponent: PercentInput,
      inputProps: {
        label: 'ANNUAL RATE OF RETURN',
        placeholder: 'Enter percent',
      },
      isRequired: true,
    },
    {
      field: 'gainsTaxRate',
      title: 'Gains Tax Rate',
      description: (
        <>
          The tax rate applied to profits from selling investments. For more info, view this{' '}
          <a
            href="https://www.irs.gov/taxtopics/tc409"
            target="_blank"
            rel="noreferrer"
            className="text-purple underline"
          >
            IRS article
          </a>
          .
        </>
      ),
      inputComponent: PercentInput,
      inputProps: {
        label: 'GAINS TAX RATE',
        placeholder: 'Enter percent',
      },
      isRequired: true,
    },
  ];

  const currentStepConfig = steps[currentStep - 1];

  const validateCurrentStep = useCallback(() => {
    const currentField = currentStepConfig.field;
    const currentValue = formState[currentField];

    if (currentStepConfig.isRequired && (currentValue === null || currentValue === undefined)) {
      return false;
    }

    if (currentValue !== null && currentValue < 0) {
      return false;
    }

    return true;
  }, [formState, currentStepConfig]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) {
      return;
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      onSubmit();
    }
  }, [currentStep, validateCurrentStep, onSubmit]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else if (onBack) {
      onBack();
    }
  }, [currentStep, onBack]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      }
    },
    [handleNext],
  );

  const isLastStep = currentStep === TOTAL_STEPS;
  const canProceed = validateCurrentStep();

  const stepContent = useMemo(() => {
    const { field, inputComponent: InputComponent, inputProps } = currentStepConfig;

    return (
      <div onKeyDown={handleKeyDown}>
        <InputComponent
          {...inputProps}
          inputRef={inputRef}
          autoFocus
          value={formState[field]}
          onChange={onChangeField(field)}
          onPreview={onChangeField(field)}
        />
        {formErrors[field] && <p className="ml-2 mt-2 text-sm text-red-400">{formErrors[field]}</p>}
      </div>
    );
  }, [currentStepConfig, formState, formErrors, onChangeField, handleKeyDown]);

  return (
    <div className="space-y-6">
      <StepProgressIndicator
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
      />

      <div
        key={`title-${currentStep}`}
        className="animate-in fade-in slide-in-from-top-2 space-y-1 text-center duration-300"
      >
        <h3 className="text-base font-medium leading-5 text-gray-900">{currentStepConfig.title}</h3>
        {currentStepConfig.description && (
          <p className="text-sm text-gray-700">{currentStepConfig.description}</p>
        )}
      </div>

      <div
        key={`content-${currentStep}`}
        className="animate-in fade-in slide-in-from-bottom-3 mt-4 duration-300"
      >
        {stepContent}
      </div>

      <div className={cn('grid gap-3', currentStep === 1 ? 'grid-cols-1' : 'grid-cols-2')}>
        {currentStep > 1 && (
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="rounded-full"
          >
            Back
          </Button>
        )}
        <Button
          type="button"
          onClick={handleNext}
          disabled={!canProceed}
          className={cn(currentStep === 1 && 'w-full', 'rounded-full')}
        >
          {isLastStep ? 'Done' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
